
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { MatchResult, MatchingRun, OutreachContent } from '../../context/MatchingContext';
import { mapMatchRow, MATCH_ROW_SELECT, isTerminalTaskStatus, isValidatedStatus } from '../../lib/mappers/match.mapper';
import { usePipeline } from '../../hooks/usePipeline';
import ScoreExplanation from '../shared/ScoreExplanation';
import CandidateValidation from '../shared/CandidateValidation';
import AiAnalysis from '../shared/AiAnalysis';
import {
    Trophy, Target, ShieldAlert, ChevronDown, ChevronUp,
    AlertTriangle, CheckCircle2, XCircle, MinusCircle, Ban, Zap, ExternalLink,
    FileText, Mail, Users, Loader2, Phone, MapPin, X, ArrowRight, ArrowUpRight, ArrowUpDown,
    Sparkles, Copy, Check, Linkedin, PenLine, Clock, Euro, SquareCheck, Square, FileDown, Columns2, Plus,
} from 'lucide-react';

interface Props {
    missionId: number;
    onLaunchMatching?: () => void;
}

function parseAvailability(raw: string | undefined | null): { label: string; dotCls: string; textCls: string; badgeCls: string } {
    if (!raw) return { label: 'Non renseignée', dotCls: 'bg-slate-200', textCls: 'text-slate-300 italic', badgeCls: 'text-slate-400 bg-slate-50 border-slate-100' };
    const v = raw.toLowerCase().trim();
    if (v === 'unavailable' || v === 'indisponible' || v.includes('indisponible') || v.includes('not available') || v.includes('occupé')) {
        return { label: 'Indisponible', dotCls: 'bg-slate-300', textCls: 'text-slate-400 font-bold', badgeCls: 'text-slate-500 bg-slate-100 border-slate-200' };
    }
    if (v === 'listening' || v.includes("l'écoute") || v.includes('écoute') || v.includes('ecoute') || v.includes('open to')) {
        return { label: "À l'écoute", dotCls: 'bg-orange-400', textCls: 'text-orange-700 font-bold', badgeCls: 'text-orange-700 bg-orange-50 border-orange-100' };
    }
    if (v === 'disponible' || v.includes('available') || v.includes('disponible') || v.includes('dispo') || v.startsWith('immédiat') || v.startsWith('immediat')) {
        return { label: 'Disponible', dotCls: 'bg-emerald-400', textCls: 'text-emerald-700 font-bold', badgeCls: 'text-emerald-600 bg-emerald-50 border-emerald-100' };
    }
    return { label: raw, dotCls: 'bg-slate-200', textCls: 'text-slate-500', badgeCls: 'text-slate-500 bg-slate-50 border-slate-100' };
}

const PLACEMENT_STATUSES_MMR = [
    { value: '',           label: 'À évaluer',        bg: 'bg-slate-100',   text: 'text-slate-500'   },
    { value: 'shortlisted', label: 'Shortlisté',       bg: 'bg-blue-100',    text: 'text-blue-700'    },
    { value: 'contacted',   label: 'Contacté',         bg: 'bg-indigo-100',  text: 'text-indigo-700'  },
    { value: 'interview',   label: 'Entretien',        bg: 'bg-violet-100',  text: 'text-violet-700'  },
    { value: 'presented',   label: 'Présenté client',  bg: 'bg-amber-100',   text: 'text-amber-700'   },
    { value: 'offer',       label: 'Offre',            bg: 'bg-orange-100',  text: 'text-orange-700'  },
    { value: 'placed',      label: 'Placé',            bg: 'bg-emerald-100', text: 'text-emerald-700' },
    { value: 'rejected',    label: 'Refusé',           bg: 'bg-red-100',     text: 'text-red-700'     },
] as const;

const MissionMatchResults: React.FC<Props> = ({ missionId, onLaunchMatching }) => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [runs, setRuns] = useState<MatchingRun[]>([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(new Set());
    const [contactCandidate, setContactCandidate] = useState<MatchResult | null>(null);
    const [historyOrder, setHistoryOrder] = useState<'desc' | 'asc'>('desc');
    const [outreachTab, setOutreachTab] = useState<'email' | 'linkedin_short' | 'linkedin_full' | 'hook' | 'contact'>('email');
    const [copiedOutreach, setCopiedOutreach] = useState<string | null>(null);
    const [isGeneratingOutreach, setIsGeneratingOutreach] = useState(false);
    const [outreachError, setOutreachError] = useState<string | null>(null);
    const [placementOverrides, setPlacementOverrides] = useState<Map<string, string>>(new Map());
    const [noteValues, setNoteValues] = useState<Map<string, string>>(new Map());
    const noteTimers = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const [selectedForBrief, setSelectedForBrief] = useState<Set<string>>(new Set());
    const [showBriefModal, setShowBriefModal] = useState(false);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [copiedBrief, setCopiedBrief] = useState(false);
    const [missionDealBreakers, setMissionDealBreakers] = useState<string[]>([]);
    const [missionTjm, setMissionTjm] = useState<string>('');
    const [liveTaskId, setLiveTaskId] = useState<number | null>(null);
    const [liveProgress, setLiveProgress] = useState<{ completed: number; failed: number; total: number } | null>(null);

    // Pipeline ATS: entries pour savoir si un candidat est déjà dans le pipeline (D-05)
    const { entries: pipelineEntries, addToPipeline } = usePipeline(missionId);

    const loadRuns = React.useCallback(async () => {
        if (!missionId || isNaN(missionId)) return;
        setIsLoading(true);
        try {
            const { data: tasks, error: taskError } = await supabase
                .from('matching_tasks')
                .select('id, created_at, status')
                .eq('mission_id', missionId)
                .order('created_at', { ascending: false });

            if (taskError) console.warn('[MissionMatchResults] tasks error:', taskError);
            if (!tasks?.length) { setRuns([]); return; }

            const loaded = await Promise.all(tasks.map(async (task: any) => {
                const { data: matchData } = await (supabase as any)
                    .from('matches')
                    .select(MATCH_ROW_SELECT)
                    .eq('matching_task_id', task.id)
                    .order('match_score', { ascending: false })
                    .limit(50);

                // P0.6 — mapping délégué au mapper central (lib/mappers/match.mapper.ts)
                const candidates: MatchResult[] = (matchData || []).map(mapMatchRow);

                return {
                    task_id: task.id,
                    created_at: task.created_at,
                    matched_count: candidates.length,
                    candidates,
                    status: task.status,
                } as MatchingRun;
            }));

            setRuns(loaded);

            const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 min — task is dead if no update since
            const inProgress = loaded.find((r: MatchingRun) => {
                if (!r.status) return false;
                if (isTerminalTaskStatus(r.status)) return false;
                // Guard against zombie tasks that are stuck and will never complete
                const ageMs = Date.now() - new Date(r.created_at).getTime();
                return ageMs < STALE_THRESHOLD_MS;
            });
            if (inProgress) {
                setLiveTaskId(inProgress.task_id);
                const { data: prog } = await (supabase as any)
                    .from('matching_tasks')
                    .select('total_jobs, completed_jobs, failed_jobs')
                    .eq('id', inProgress.task_id)
                    .single();
                if (prog) setLiveProgress({ completed: (prog as any).completed_jobs ?? 0, failed: (prog as any).failed_jobs ?? 0, total: (prog as any).total_jobs ?? 0 });
            } else {
                setLiveTaskId(null);
                setLiveProgress(null);
            }
        } catch (e) {
            console.warn('[MissionMatchResults] load error:', e);
        } finally {
            setIsLoading(false);
        }
    }, [missionId]);

    useEffect(() => {
        setRuns([]);
        loadRuns();
    }, [loadRuns]);

    useEffect(() => {
        if (!missionId || isNaN(missionId)) return;
        supabase.from('missions' as any).select('summary_json').eq('id', missionId).single()
            .then(({ data }: any) => {
                if (!data?.summary_json) return;
                const sj = typeof data.summary_json === 'string' ? JSON.parse(data.summary_json) : data.summary_json;
                const d = Array.isArray(sj) ? sj[0] : sj;
                const raw = d?.raw || {};
                const profile = d?.profile || {};
                setMissionDealBreakers(profile.sourcing_strategy?.deal_breakers || d?.dealBreakers || []);
                const tjmStr = raw.tjm_min && raw.tjm_max ? `${raw.tjm_min} - ${raw.tjm_max} ${raw.tjm_currency || '€'}` : '';
                setMissionTjm(tjmStr);
            });
    }, [missionId]);

    // Realtime : nouvelle tâche démarrée pour cette mission (INSERT sur matching_tasks)
    useEffect(() => {
        if (!missionId || isNaN(missionId)) return;

        const newTaskChannel = supabase
            .channel(`mission-tasks-${missionId}`)
            .on('postgres_changes' as any, {
                event: 'INSERT',
                schema: 'public',
                table: 'matching_tasks',
                filter: `mission_id=eq.${missionId}`,
            }, (payload: any) => {
                const t = payload.new;
                setRuns(prev => [{
                    task_id: t.id,
                    created_at: t.created_at,
                    matched_count: 0,
                    candidates: [],
                    status: t.status,
                } as MatchingRun, ...prev]);
                setLiveTaskId(t.id);
                setLiveProgress({ completed: 0, failed: 0, total: t.total_jobs ?? 0 });
            })
            .subscribe();

        return () => { supabase.removeChannel(newTaskChannel); };
    }, [missionId]);

    // Realtime : candidats en direct + progression pour la tâche courante
    useEffect(() => {
        if (!liveTaskId) return;

        const matchChannel = supabase
            .channel(`matches-live-${liveTaskId}`)
            .on('postgres_changes' as any, {
                event: 'INSERT',
                schema: 'public',
                table: 'matches',
                filter: `matching_task_id=eq.${liveTaskId}`,
            }, async (payload: any) => {
                const m = payload.new;
                if (m.matching_task_id !== liveTaskId) return;

                const { data: cand } = await (supabase as any)
                    .from('candidates')
                    .select('full_name, summary_json, availability_status, tjm_min, tjm_max')
                    .eq('id', m.candidate_id)
                    .single();

                // P0.6 — mapping délégué au mapper central ; rank recalculé après tri ci-dessous
                const newResult: MatchResult = { ...mapMatchRow({ ...m, candidates: cand }, 0), rank: 0 };

                setRuns(prev => prev.map(r => {
                    if (r.task_id !== liveTaskId) return r;
                    if (r.candidates.some(c => c.candidate_id === newResult.candidate_id)) return r;
                    const updated = [...r.candidates, newResult]
                        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
                        .map((c, i) => ({ ...c, rank: i + 1 }));
                    return { ...r, matched_count: updated.length, candidates: updated };
                }));
            })
            .subscribe();

        const taskChannel = supabase
            .channel(`task-progress-${liveTaskId}`)
            .on('postgres_changes' as any, {
                event: 'UPDATE',
                schema: 'public',
                table: 'matching_tasks',
                filter: `id=eq.${liveTaskId}`,
            }, (payload: any) => {
                const t = payload.new;
                setLiveProgress({
                    completed: t.completed_jobs ?? 0,
                    failed: t.failed_jobs ?? 0,
                    total: t.total_jobs ?? 0,
                });
                setRuns(prev => prev.map(r =>
                    r.task_id === liveTaskId ? { ...r, status: t.status } : r
                ));
                if (isTerminalTaskStatus(t.status)) {
                    setLiveTaskId(null);
                    setLiveProgress(null);
                    loadRuns();
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(matchChannel);
            supabase.removeChannel(taskChannel);
        };
    }, [liveTaskId, loadRuns]);

    const toggleRow = (key: string) => setExpandedRowKeys(prev => {
        const next = new Set(prev); prev.has(key) ? next.delete(key) : next.add(key); return next;
    });

    // ── Sub-section accordions ────────────────────────────────────────────────
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const toggleSection = (key: string) => setExpandedSections(prev => {
        const next = new Set(prev); prev.has(key) ? next.delete(key) : next.add(key); return next;
    });
    const isSectionOpen = (key: string) => expandedSections.has(key);

    const getPlacementMMR = (c: MatchResult): string =>
        placementOverrides.get(`${c.matching_task_id}-${c.candidate_id}`) ?? c.placement_status ?? '';

    const getPlacementInfoMMR = (val: string) =>
        PLACEMENT_STATUSES_MMR.find(s => s.value === val) ?? PLACEMENT_STATUSES_MMR[0];

    const updatePlacementMMR = async (c: MatchResult, val: string) => {
        const key = `${c.matching_task_id}-${c.candidate_id}`;
        setPlacementOverrides(prev => new Map(prev).set(key, val));
        if (c.matching_task_id && c.candidate_id) {
            await supabase.from('matches' as any)
                .update({ placement_status: val || null })
                .eq('matching_task_id', c.matching_task_id)
                .eq('candidate_id', c.candidate_id);
        }
    };

    const getNoteMMR = (c: MatchResult): string => {
        const key = `${c.matching_task_id}-${c.candidate_id}`;
        return noteValues.has(key) ? noteValues.get(key)! : (c.consultant_note ?? '');
    };

    const handleNoteChangeMMR = (c: MatchResult, val: string) => {
        const key = `${c.matching_task_id}-${c.candidate_id}`;
        setNoteValues(prev => new Map(prev).set(key, val));
        const existing = noteTimers.current.get(key);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(async () => {
            if (c.matching_task_id && c.candidate_id) {
                await supabase.from('matches' as any)
                    .update({ consultant_note: val || null })
                    .eq('matching_task_id', c.matching_task_id)
                    .eq('candidate_id', c.candidate_id);
            }
        }, 800);
        noteTimers.current.set(key, timer);
    };

    // Meilleurs profils : meilleur score par candidat, filtrés sur EXCELLENT MATCH ou BON MATCH
    const topCandidates: MatchResult[] = (() => {
        const byId = new Map<number, MatchResult>();
        for (const run of runs) {
            for (const c of run.candidates) {
                if (!c.candidate_id) continue;
                const existing = byId.get(c.candidate_id);
                if (!existing || (c.score ?? 0) > (existing.score ?? 0)) {
                    byId.set(c.candidate_id, c);
                }
            }
        }
        return Array.from(byId.values())
            .filter(c => c.recommendation_status === 'EXCELLENT MATCH' || c.recommendation_status === 'BON MATCH')
            .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    })();

    const getCriteriaIcon = (status: string, importance?: string) => {
        if (status === 'VALIDÉ') return <CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" />;
        if (status === 'VALIDÉ_PAR_DÉDUCTION') return <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0" />;
        if (status === 'PARTIEL') return <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />;
        if (importance?.toLowerCase().includes('nice')) return <MinusCircle size={15} className="text-slate-300 mt-0.5 shrink-0" />;
        return <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />;
    };

    const tableHeader = (
        <div className="hidden md:grid grid-cols-[2fr_110px_140px_160px_44px] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <span>Candidat</span>
            <span className="text-center">Score</span>
            <span className="text-center">Critères</span>
            <span className="text-center">Statut</span>
            <span />
        </div>
    );

    const renderRow = (c: MatchResult, rowKey: string, rank: number) => {
        const isExpanded = expandedRowKeys.has(rowKey);
        const criteria = c.criteria_analysis ?? [];
        const mustCriteria = criteria.filter(cr => cr.importance?.toUpperCase().includes('MUST'));
        const niceCriteria = criteria.filter(cr => !cr.importance?.toUpperCase().includes('MUST'));
        const mustValidated = mustCriteria.filter(cr => isValidatedStatus(cr.candidate_status)).length;
        const mustPartial = mustCriteria.filter(cr => cr.candidate_status === 'PARTIEL').length;
        const mustMissing = mustCriteria.length - mustValidated - mustPartial;
        const niceValidated = niceCriteria.filter(cr => isValidatedStatus(cr.candidate_status)).length;
        const mustRatio = mustCriteria.length > 0 ? mustValidated / mustCriteria.length : null;
        const mustColorText = mustRatio === null ? 'text-slate-700' : mustRatio > 0.8 ? 'text-emerald-600' : mustRatio >= 0.25 ? 'text-amber-500' : 'text-red-600';
        const mustColorBadge = mustRatio === null ? 'bg-slate-100 text-slate-500' : mustRatio > 0.8 ? 'bg-emerald-100 text-emerald-600' : mustRatio >= 0.25 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600';
        const name = c.name ?? `Candidat #${c.candidate_id}`;
        const initials = name.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase();
        const isChampion = rank === 1;
        const score = c.score ?? 0;
        const scoreColor = score >= 80 ? 'bg-emerald-500' : score >= 65 ? 'bg-amber-400' : 'bg-slate-300';
        const placement = getPlacementMMR(c);
        const placementInfo = getPlacementInfoMMR(placement);

        return (
            <div key={rowKey} className={`border-l-4 transition-colors ${isChampion ? 'border-l-amber-400' : 'border-l-transparent'}`}>
                {/* Mobile row */}
                <div className="md:hidden flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                    <button
                        onClick={e => { e.stopPropagation(); const key = `${c.matching_task_id}-${c.candidate_id}`; setSelectedForBrief(prev => { const next = new Set(prev); prev.has(key) ? next.delete(key) : (next.size < 3 && next.add(key)); return next; }); }}
                        className="shrink-0 text-slate-300 hover:text-amber-500 transition-colors"
                    >
                        {selectedForBrief.has(`${c.matching_task_id}-${c.candidate_id}`) ? <SquareCheck size={16} className="text-amber-500" /> : <Square size={16} />}
                    </button>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 border-2 ${isChampion ? 'bg-slate-900 text-white border-amber-400' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {isChampion ? <Trophy size={13} className="text-amber-400" fill="currentColor" /> : initials}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-800 text-sm truncate">{name}</p>
                        {c.title && <p className="text-xs text-slate-400 truncate">{c.title}</p>}
                        {mustCriteria.length > 0 && (
                            <span className="flex items-center gap-1 text-[10px] font-bold">
                                <span className="text-emerald-600">{mustValidated}</span>
                                {mustPartial > 0 && <span className="text-amber-500">{mustPartial}</span>}
                                {mustMissing > 0 && <span className="text-red-400">{mustMissing}</span>}
                                <span className="text-slate-400">/{mustCriteria.length} MUST</span>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-lg font-black ${isChampion ? 'text-emerald-600' : 'text-slate-700'}`}>{score}%</span>
                        <button onClick={() => toggleRow(rowKey)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                            {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                    </div>
                </div>
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-[2fr_110px_140px_160px_44px] gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={e => { e.stopPropagation(); const key = `${c.matching_task_id}-${c.candidate_id}`; setSelectedForBrief(prev => { const next = new Set(prev); prev.has(key) ? next.delete(key) : (next.size < 3 && next.add(key)); return next; }); }}
                            className="shrink-0 text-slate-300 hover:text-amber-500 transition-colors"
                            title="Sélectionner pour la note client"
                        >
                            {selectedForBrief.has(`${c.matching_task_id}-${c.candidate_id}`) ? <SquareCheck size={16} className="text-amber-500" /> : <Square size={16} />}
                        </button>
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border-2 ${isChampion ? 'bg-slate-900 text-white border-amber-400' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                            {isChampion ? <Trophy size={16} className="text-amber-400" fill="currentColor" /> : initials}
                        </div>
                        <div className="min-w-0">
                            <p className="font-bold text-slate-800 text-base truncate">{name}</p>
                            {c.title && <p className="text-sm text-slate-400 truncate">{c.title}</p>}
                            {(c.verdict_teaser || c.verdict_short) && (
                                <p className="text-xs text-slate-500 font-medium truncate mt-0.5 leading-snug">{c.verdict_teaser || c.verdict_short}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {c.availability && (() => { const av = parseAvailability(c.availability as string | undefined); return (
                                    <span className={`flex items-center gap-1 text-[10px] font-medium border rounded px-1.5 py-0.5 ${av.badgeCls}`}>
                                        <Clock size={9} /> {av.label}
                                    </span>
                                ); })()}
                                {(c.tjm_min || c.tjm_max) && (
                                    <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">
                                        <Euro size={9} />
                                        {c.tjm_min && c.tjm_max ? `${c.tjm_min}–${c.tjm_max}€/j` : c.tjm_min ? `≥${c.tjm_min}€/j` : `≤${c.tjm_max}€/j`}
                                    </span>
                                )}
                                {/* CONSTRAINT-1 D9 — le retrait se lit sans déplier la ligne. */}
                                <ScoreExplanation breakdown={c.score_breakdown} compact />
                            </div>
                        </div>
                    </div>
                    {/* Score + mini bar */}
                    <div className="flex flex-col items-center gap-1.5">
                        <span className={`text-2xl font-black ${isChampion ? 'text-emerald-600' : 'text-slate-700'}`}>{score}%</span>
                        <div className="w-16 h-2 rounded-full bg-slate-200 overflow-hidden">
                            <div className={`h-full rounded-full ${scoreColor}`} style={{ width: `${score}%` }} />
                        </div>
                    </div>
                    {/* Critères MUST/NICE */}
                    <div className="flex flex-col gap-0.5 items-center">
                        {mustCriteria.length > 0 && (
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1 text-xs font-bold">
                                    <span className="text-emerald-600">{mustValidated}</span>
                                    {mustPartial > 0 && <span className="text-amber-500">{mustPartial}</span>}
                                    {mustMissing > 0 && <span className="text-red-400">{mustMissing}</span>}
                                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded uppercase ${mustColorBadge}`}>MUST</span>
                                </div>
                                <div className="flex gap-px w-full">
                                    {mustCriteria.map((cr, i) => (
                                        <div key={i} className={`h-1.5 flex-1 ${i === 0 ? 'rounded-l-sm' : ''} ${i === mustCriteria.length - 1 ? 'rounded-r-sm' : ''} ${isValidatedStatus(cr.candidate_status) ? 'bg-emerald-400' : cr.candidate_status === 'PARTIEL' ? 'bg-amber-400' : 'bg-red-200'}`} />
                                    ))}
                                </div>
                            </div>
                        )}
                        {niceCriteria.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-slate-400">
                                <span>{niceValidated}/{niceCriteria.length}</span>
                                <span className="text-[9px] px-1 py-0.5 rounded uppercase bg-slate-50 text-slate-400">NICE</span>
                            </div>
                        )}
                    </div>
                    {/* Statut recommendation + placement */}
                    <div className="flex flex-col items-center gap-1">
                        {c.recommendation_status && (
                            <span className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide whitespace-nowrap ${
                                c.recommendation_status === 'EXCELLENT MATCH' ? 'bg-emerald-100 text-emerald-700'
                                : c.recommendation_status === 'BON MATCH' ? 'bg-amber-100 text-amber-700'
                                : c.recommendation_status === 'RISQUÉ' ? 'bg-orange-100 text-orange-700'
                                : c.recommendation_status === 'REJETÉ' ? 'bg-red-100 text-red-600'
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                                {c.recommendation_status === 'EXCELLENT MATCH' ? 'Excellent'
                                : c.recommendation_status === 'BON MATCH' ? 'Bon match'
                                : c.recommendation_status === 'RISQUÉ' ? 'Risqué'
                                : c.recommendation_status === 'REJETÉ' ? 'Rejeté'
                                : c.recommendation_status}
                            </span>
                        )}
                        {placement && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${placementInfo.bg} ${placementInfo.text}`}>
                                {placementInfo.label}
                            </span>
                        )}
                        {/* Bouton Ajouter au pipeline (D-05, D-06) */}
                        {(() => {
                            const isAlreadyInPipeline = pipelineEntries.some(
                                (e) => e.candidateId === c.candidate_id
                            );
                            return isAlreadyInPipeline ? (
                                <button
                                    disabled
                                    className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg cursor-default"
                                    title="Déjà dans le pipeline"
                                >
                                    <CheckCircle2 size={14} />
                                    Ajouté au pipeline
                                </button>
                            ) : (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (c.candidate_id) addToPipeline(c.candidate_id);
                                    }}
                                    className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                                    title="Ajouter au pipeline de recrutement"
                                >
                                    <Plus size={14} />
                                    Ajouter au pipeline
                                </button>
                            );
                        })()}
                    </div>
                    <button onClick={() => toggleRow(rowKey)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </div>

                {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50 p-5 space-y-5">

                        {/* Le calcul à gauche, l'avis à droite — même disposition que la vue
                            moteur, pour qu'un même candidat se lise pareil dans les deux écrans. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
                            <ScoreExplanation breakdown={c.score_breakdown} score={score} />
                            <AiAnalysis verdict={c.verdict_full} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {(c.criteria_analysis?.length ?? 0) > 0 && (() => {
                                const mmrMust = (c.criteria_analysis ?? []).filter(cr => cr.importance?.toUpperCase().includes('MUST'));
                                const mmrNice = (c.criteria_analysis ?? []).filter(cr => !cr.importance?.toUpperCase().includes('MUST'));
                                return (
                                    <div>
                                        <h4 className="font-bold text-slate-700 mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide">
                                            <Target size={12} /> Adéquation aux Critères
                                        </h4>
                                        {/* MUST always visible */}
                                        {mmrMust.length > 0 && (
                                            <ul className="space-y-2 mb-2">
                                                {mmrMust.map((cr, idx) => (
                                                    <li key={idx} className="flex items-start gap-2">
                                                        {getCriteriaIcon(cr.candidate_status, cr.importance)}
                                                        <div>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="font-bold text-slate-800 text-xs">{cr.criterion_name}</span>
                                                                <span className="text-[8px] font-bold bg-slate-200 text-slate-500 px-1 rounded uppercase">MUST</span>
                                                            </div>
                                                            <div className="text-[11px] text-slate-500 italic leading-tight">{cr.comment}</div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {/* NICE collapsible */}
                                        {mmrNice.length > 0 && (
                                            <div>
                                                <button
                                                    onClick={() => toggleSection(`${rowKey}:nice`)}
                                                    className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors mb-1"
                                                >
                                                    {isSectionOpen(`${rowKey}:nice`) ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                                    {isSectionOpen(`${rowKey}:nice`) ? 'Masquer' : `Voir ${mmrNice.length} critère${mmrNice.length > 1 ? 's' : ''} NICE`}
                                                </button>
                                                {isSectionOpen(`${rowKey}:nice`) && (
                                                    <ul className="space-y-2">
                                                        {mmrNice.map((cr, idx) => (
                                                            <li key={idx} className="flex items-start gap-2">
                                                                {getCriteriaIcon(cr.candidate_status, cr.importance)}
                                                                <div>
                                                                    <div className="font-bold text-slate-800 text-xs">{cr.criterion_name}</div>
                                                                    <div className="text-[11px] text-slate-500 italic leading-tight">{cr.comment}</div>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                        {/* Fallback: no importance tags */}
                                        {mmrMust.length === 0 && mmrNice.length === 0 && (
                                            <ul className="space-y-2">
                                                {c.criteria_analysis!.map((cr, idx) => (
                                                    <li key={idx} className="flex items-start gap-2">
                                                        {getCriteriaIcon(cr.candidate_status, cr.importance)}
                                                        <div>
                                                            <div className="font-bold text-slate-800 text-xs">{cr.criterion_name}</div>
                                                            <div className="text-[11px] text-slate-500 italic leading-tight">{cr.comment}</div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })()}
                            <div className="space-y-3">
                                {/* Deal Breakers mission — verdicts par item (KNOCKOUT-1), fallback heuristique pour anciens matchs */}
                                {((c.deal_breakers_analysis?.length ?? 0) > 0 || missionDealBreakers.length > 0) && (() => {
                                    const dba = c.deal_breakers_analysis ?? [];
                                    const useDba = dba.length > 0;
                                    const criteria = c.criteria_analysis ?? [];
                                    const isKnockout = !!(c as any).score_breakdown?.is_knockout;
                                    // Fallback (anciens matchs sans deal_breakers_analysis) : heuristique mots-clés
                                    const checkDb = (db: string): 'pass' | 'fail' | 'partial' | 'unknown' => {
                                        const words = db.toLowerCase().split(/[\s,;/()[\]]+/).filter((w: string) => w.length > 3);
                                        const knockoutReason = ((c as any).score_breakdown?.knockout_reason ?? '').toLowerCase();
                                        if (isKnockout && knockoutReason) {
                                            const inKnockout = words.filter((w: string) => w.length > 5).some((w: string) => knockoutReason.includes(w));
                                            if (inKnockout) return 'fail';
                                        }
                                        for (const cr of criteria) {
                                            const crLower = cr.criterion_name.toLowerCase();
                                            if (words.some((w: string) => crLower.includes(w))) {
                                                if (isValidatedStatus(cr.candidate_status)) return 'pass';
                                                if (cr.candidate_status === 'PARTIEL') return 'partial';
                                                return 'fail';
                                            }
                                        }
                                        return isKnockout ? 'unknown' : 'pass';
                                    };
                                    // verdict moteur → statut d'affichage
                                    const vStatus = (v: string): 'pass' | 'fail' | 'partial' | 'unknown' =>
                                        v === 'respecté' ? 'pass' : v === 'bloquant' ? 'fail' : v === 'à vérifier' ? 'partial' : 'unknown';
                                    const rows: Array<{ label: string; status: 'pass' | 'fail' | 'partial' | 'unknown'; evidence?: string }> =
                                        useDba
                                            ? dba.map((d) => ({ label: d.deal_breaker, status: vStatus(d.verdict), evidence: d.evidence }))
                                            : missionDealBreakers.map((db: string) => ({ label: db, status: checkDb(db) }));
                                    return (
                                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                                                <ShieldAlert size={11} className="text-red-500" />
                                                <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Deal Breakers mission</h4>
                                            </div>
                                            <div className="divide-y divide-slate-50">
                                                {rows.map((r, i: number) => (
                                                    <div key={i} className="px-3 py-2">
                                                        <div className="flex items-start gap-2">
                                                            {r.status === 'pass'    && <CheckCircle2 size={13} className="text-emerald-500 mt-0.5 shrink-0" />}
                                                            {r.status === 'partial' && <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />}
                                                            {r.status === 'fail'    && <Ban size={13} className="text-red-500 mt-0.5 shrink-0" />}
                                                            {r.status === 'unknown' && <MinusCircle size={13} className="text-slate-300 mt-0.5 shrink-0" />}
                                                            <p className="text-xs text-slate-700 leading-snug">{r.label}</p>
                                                        </div>
                                                        {r.evidence && <p className="text-[11px] text-slate-400 leading-snug ml-[21px] mt-0.5">{r.evidence}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                                                <span className="flex items-center gap-1 text-[9px] text-slate-500"><CheckCircle2 size={9} className="text-emerald-500" /> Respecté</span>
                                                <span className="flex items-center gap-1 text-[9px] text-slate-500"><AlertTriangle size={9} className="text-amber-500" /> À vérifier</span>
                                                <span className="flex items-center gap-1 text-[9px] text-slate-500"><Ban size={9} className="text-red-500" /> Bloquant</span>
                                                <span className="flex items-center gap-1 text-[9px] text-slate-400"><MinusCircle size={9} className="text-slate-300" /> Non évalué</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                                {/* TJM — comparaison candidat vs mission */}
                                {((c.tjm_min ?? 0) > 0 || (c.tjm_max ?? 0) > 0 || missionTjm) && (
                                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                                            <Euro size={11} className="text-blue-500" />
                                            <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">TJM — comparaison</h4>
                                        </div>
                                        {(() => {
                                            const missionNums = missionTjm?.match(/\d+/g)?.map(Number) ?? [];
                                            const missionMin = missionNums.length > 0 ? Math.min(...missionNums) : null;
                                            const missionMax = missionNums.length > 0 ? Math.max(...missionNums) : null;
                                            const candMin = (c.tjm_min ?? 0) > 0 ? c.tjm_min! : null;
                                            const candMax = (c.tjm_max ?? 0) > 0 ? c.tjm_max! : null;
                                            const isOver = candMin !== null && missionMax !== null && candMin > missionMax;
                                            const isMatch = candMin !== null && missionMax !== null && candMin <= missionMax;
                                            const allNums = [missionMin, missionMax, candMin, candMax].filter((v): v is number => v !== null);
                                            const globalMax = allNums.length > 0 ? Math.max(...allNums) * 1.1 : 1000;
                                            const pct = (v: number) => Math.min(100, Math.round((v / globalMax) * 100));
                                            return (
                                                <div className="p-3 space-y-3">
                                                    <div>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="text-[10px] font-bold text-slate-600">Candidat</span>
                                                            <span className={`text-xs font-black ${isOver ? 'text-red-600' : 'text-slate-800'}`}>
                                                                {candMin && candMax ? `${candMin} – ${candMax} €/j` : candMin ? `≥ ${candMin} €/j` : candMax ? `≤ ${candMax} €/j` : '—'}
                                                            </span>
                                                        </div>
                                                        {(candMin || candMax) && (
                                                            <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`absolute h-full rounded-full ${isOver ? 'bg-red-400' : 'bg-blue-400'}`}
                                                                    style={{ left: `${pct(candMin ?? (candMax ?? 0))}%`, width: `${pct((candMax ?? candMin ?? 0) - (candMin ?? 0))}%`, minWidth: '8px' }}
                                                                />
                                                                {candMin && <span className="absolute left-1 top-0 h-full flex items-center text-[9px] font-bold text-white drop-shadow">{candMin}€</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {missionMin !== null && missionMax !== null && (
                                                        <div>
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-[10px] font-bold text-slate-600">Mission</span>
                                                                <span className="text-xs font-bold text-slate-500">{missionTjm}</span>
                                                            </div>
                                                            <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className="absolute h-full rounded-full bg-emerald-400"
                                                                    style={{ left: `${pct(missionMin)}%`, width: `${pct(missionMax - missionMin)}%`, minWidth: '8px' }}
                                                                />
                                                                <span className="absolute left-1 top-0 h-full flex items-center text-[9px] font-bold text-white drop-shadow">{missionMin}€</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isOver && (
                                                        <div className="flex items-center gap-1.5 text-[10px] text-red-700 font-semibold bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                                                            <AlertTriangle size={11} /> TJM candidat supérieur au budget mission — à négocier
                                                        </div>
                                                    )}
                                                    {isMatch && !isOver && (
                                                        <div className="flex items-center gap-1.5 text-[10px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                                                            <CheckCircle2 size={11} /> TJM dans le budget mission
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Pleine largeur : c'est le bloc qui ferme le panneau,
                            et ses deux colonnes se répondent. */}
                        <CandidateValidation
                            objections={c.objection_handling}
                            questions={c.verification_questions}
                        />
                        {/* Note personnelle */}
                        <div className="pt-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1 mb-1">
                                <PenLine size={10} /> Note consultant
                            </label>
                            <textarea
                                value={getNoteMMR(c)}
                                onChange={e => handleNoteChangeMMR(c, e.target.value)}
                                placeholder="Ex: Cherche full-remote, à rappeler après le 15, présenté au client…"
                                rows={2}
                                className="w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 placeholder:text-slate-300"
                            />
                        </div>
                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                            <button onClick={() => c.candidate_id && navigate(`/talents/${c.candidate_id}`)} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                                <FileText size={13} /> Voir le Profil
                            </button>
                            <button
                                onClick={() => {
                                    setContactCandidate(c);
                                    const hasOutreach = c.outreach?.email || c.outreach?.linkedin_dm_short || c.outreach?.hook || c.sales_pitch;
                                    setOutreachTab(hasOutreach ? 'email' : 'contact');
                                }}
                                className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 rounded-lg text-xs font-bold text-white transition-colors"
                            >
                                <Sparkles size={13} /> Outreach IA
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ── Loading ──────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
                <Loader2 size={20} className="animate-spin text-amber-500" />
                <span className="text-sm">Chargement des résultats...</span>
            </div>
        );
    }

    // ── Empty ────────────────────────────────────────────────────────────────
    if (runs.length === 0) {
        return (
            <div className="text-center p-12 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-14 h-14 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap size={28} />
                </div>
                <h4 className="text-slate-900 font-bold mb-2">Aucun matching effectué</h4>
                <p className="text-slate-500 text-sm mb-6">Lancez une analyse pour identifier les meilleurs profils de votre base de talents.</p>
                {onLaunchMatching && (
                    <button
                        onClick={onLaunchMatching}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-sm transition-colors"
                    >
                        <Zap size={18} /> Lancer le matching
                    </button>
                )}
            </div>
        );
    }

    const sortedRuns = historyOrder === 'desc' ? runs : [...runs].reverse();
    const recentRuns = sortedRuns.slice(0, 5);

    return (
        <div className="flex flex-col lg:flex-row gap-5 items-stretch">

            {/* ── Left: Meilleurs Profils ───────────────────────────────────── */}
            <section className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 flex-wrap">
                        <Trophy size={14} className="text-amber-500" fill="currentColor" />
                        Meilleurs Profils
                        <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                            {topCandidates.length} profil{topCandidates.length > 1 ? 's' : ''} qualifié{topCandidates.length > 1 ? 's' : ''}
                        </span>
                        {liveTaskId && liveProgress && (
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                                <Loader2 size={11} className="animate-spin" />
                                {liveProgress.completed}/{liveProgress.total} analysés
                            </span>
                        )}
                    </h3>
                    <div className="flex items-center gap-2">
                        {onLaunchMatching && (
                            <button
                                onClick={() => navigate('/matching', { state: { preSelectedMissionId: missionId } })}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition-all shrink-0"
                                title="Relancer une analyse IA sur toute la base"
                            >
                                <Sparkles size={13} /> Nouveau matching
                            </button>
                        )}
                        {selectedForBrief.size > 0 && (
                            <>
                                {selectedForBrief.size >= 2 && (
                                    <button
                                        onClick={() => setShowCompareModal(true)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors shrink-0"
                                    >
                                        <Columns2 size={13} /> Comparer ({selectedForBrief.size})
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowBriefModal(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg shadow-sm transition-colors shrink-0"
                                >
                                    <FileDown size={13} /> Note client ({selectedForBrief.size})
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {topCandidates.length === 0 ? (
                    (() => {
                        // Le moteur écrit "error: <détail>" — startsWith requis (fix P0.6)
                        const hasStuck = runs.some(r => r.matched_count === 0 && ((r.status ?? '').startsWith('error') || !isTerminalTaskStatus(r.status)));
                        return hasStuck ? (
                            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-200 text-sm text-red-700">
                                <AlertTriangle size={16} className="shrink-0 text-red-400" />
                                <span>L'analyse a échoué (timeout Gemini). <button onClick={onLaunchMatching} className="underline font-semibold hover:text-red-900">Relancez le matching</button> pour obtenir les résultats.</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-500">
                                <Users size={16} className="shrink-0 text-slate-300" />
                                Aucun candidat trouvé dans les analyses.
                            </div>
                        );
                    })()
                ) : (
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        {tableHeader}
                        <div className="divide-y divide-slate-50">
                            {topCandidates.map((c, i) => renderRow(c, `top-${c.candidate_id}`, i + 1))}
                        </div>
                    </div>
                )}
            </section>

            {/* ── Right: Historique des analyses ───────────────────────────── */}
            <section className="w-full lg:w-80 shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        Historique
                        <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                            {runs.length} runs
                        </span>
                    </h3>
                    <button
                        onClick={() => setHistoryOrder(o => o === 'desc' ? 'asc' : 'desc')}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                        title={historyOrder === 'desc' ? 'Plus récent en premier' : 'Plus ancien en premier'}
                    >
                        <ArrowUpDown size={12} />
                        {historyOrder === 'desc' ? 'Récent' : 'Ancien'}
                    </button>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100 overflow-hidden h-full">
                    {recentRuns.map((run) => {
                        const globalIdx = runs.indexOf(run);
                        const runNumber = runs.length - globalIdx;
                        const runDate = new Date(run.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'short',
                            hour: '2-digit', minute: '2-digit',
                        });
                        const count = run.matched_count ?? run.candidates.length;
                        return (
                            <div key={run.task_id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center shrink-0">
                                        {runNumber}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-slate-700 truncate">
                                            {count} profil{count > 1 ? 's' : ''} analysé{count > 1 ? 's' : ''}
                                        </p>
                                        <p className="text-xs text-slate-400">{runDate}</p>
                                    </div>
                                </div>
                                {run.task_id === liveTaskId && liveProgress ? (
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                        <span className="text-[10px] font-bold text-amber-600 tabular-nums">
                                            {liveProgress.completed}/{liveProgress.total}
                                        </span>
                                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                                                style={{ width: `${liveProgress.total > 0 ? Math.round((liveProgress.completed / liveProgress.total) * 100) : 0}%` }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => navigate(`/matching/${run.task_id}`)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-900/5 hover:bg-slate-900 hover:text-white rounded-lg transition-all shrink-0"
                                    >
                                        Voir <ArrowRight size={12} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {runs.length > 5 && (
                        <div className="px-4 py-3 text-center border-t border-slate-100">
                            <span className="text-xs text-slate-400">+{runs.length - 5} autres runs</span>
                        </div>
                    )}
                </div>
            </section>

        {/* ── Comparaison côte-à-côte modal ────────────────────────────────── */}
        {showCompareModal && (() => {
            const compared = topCandidates.filter(c =>
                selectedForBrief.has(`${c.matching_task_id}-${c.candidate_id}`)
            );
            const allCriteriaNames = Array.from(new Set(
                compared.flatMap(c => c.criteria_analysis?.map(cr => cr.criterion_name) ?? [])
            ));
            return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowCompareModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl z-10 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <h3 className="font-bold text-slate-900 flex items-center gap-2">
                                <Columns2 size={16} className="text-violet-500" /> Comparaison côte-à-côte
                            </h3>
                            <button onClick={() => setShowCompareModal(false)} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><X size={18} /></button>
                        </div>
                        <div className="overflow-auto flex-1">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100">
                                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide w-44">Critère</th>
                                        {compared.map(c => (
                                            <th key={c.candidate_id} className="px-4 py-3 text-center min-w-[180px]">
                                                <p className="font-bold text-slate-800">{c.name}</p>
                                                <p className="text-slate-400 font-normal truncate">{c.title}</p>
                                                <p className={`text-base font-black mt-1 ${(c.score ?? 0) >= 75 ? 'text-emerald-600' : 'text-slate-600'}`}>{c.score}%</p>
                                                {(c.availability || c.tjm_min || c.tjm_max) && (
                                                    <div className="flex flex-wrap justify-center gap-1 mt-1">
                                                        {c.availability && <span className="text-[9px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded px-1 py-0.5">{c.availability}</span>}
                                                        {(c.tjm_min || c.tjm_max) && <span className="text-[9px] font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded px-1 py-0.5">{c.tjm_min && c.tjm_max ? `${c.tjm_min}–${c.tjm_max}€/j` : c.tjm_min ? `≥${c.tjm_min}€/j` : `≤${c.tjm_max}€/j`}</span>}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {allCriteriaNames.map(criterionName => (
                                        <tr key={criterionName} className="hover:bg-slate-50">
                                            <td className="px-4 py-2.5 font-semibold text-slate-700">{criterionName}</td>
                                            {compared.map(c => {
                                                const cr = c.criteria_analysis?.find(x => x.criterion_name === criterionName);
                                                if (!cr) return <td key={c.candidate_id} className="px-4 py-2.5 text-center text-slate-200">—</td>;
                                                const isMust = cr.importance?.toUpperCase().includes('MUST');
                                                return (
                                                    <td key={c.candidate_id} className="px-4 py-2.5">
                                                        <div className="flex flex-col items-center gap-1">
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                                                isValidatedStatus(cr.candidate_status) ? 'bg-emerald-100 text-emerald-700'
                                                                : cr.candidate_status === 'PARTIEL' ? 'bg-amber-100 text-amber-700'
                                                                : 'bg-red-100 text-red-700'
                                                            }`}>{cr.candidate_status === 'VALIDÉ_PAR_DÉDUCTION' ? 'VALIDÉ*' : cr.candidate_status}</span>
                                                            {isMust && <span className="text-[9px] bg-slate-100 text-slate-400 px-1 rounded uppercase">MUST</span>}
                                                            {cr.comment && <p className="text-[10px] text-slate-400 text-center leading-snug max-w-[140px]">{cr.comment}</p>}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {allCriteriaNames.length === 0 && (
                                        <tr>
                                            <td colSpan={compared.length + 1} className="px-4 py-8 text-center text-sm text-slate-400">
                                                Aucun critère disponible pour ces candidats.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        })()}

        {/* ── Synthèse client modal ────────────────────────────────────────── */}
        {showBriefModal && (() => {
            const selectedCandidates = topCandidates.filter(c =>
                selectedForBrief.has(`${c.matching_task_id}-${c.candidate_id}`)
            );
            // Default: all selected; if none selected somehow, use top ≥70%
            const briefCandidates = selectedCandidates.length > 0
                ? selectedCandidates
                : topCandidates.filter(c => (c.score ?? 0) >= 70);
            const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

            const getPitch = (c: MatchResult) =>
                c.client_summary || c.outreach?.hook || c.sales_pitch || c.verdict_teaser || '';
            const getFullPitch = (c: MatchResult) =>
                c.client_pitch || c.sales_pitch || c.outreach?.email || c.verdict_full || '';

            const briefText = [
                `NOTE DE PRÉSENTATION DES CANDIDATS`,
                `Date : ${today}`,
                ``,
                `─────────────────────────────────────────────────────`,
                `MÉTHODOLOGIE REVERTAI`,
                `Chaque candidat a été analysé par notre moteur de matching sémantique qui croise`,
                `l'ensemble du profil avec les exigences de la mission. Le score de fit reflète`,
                `l'adéquation globale sur les critères MUST et NICE définis.`,
                `─────────────────────────────────────────────────────`,
                ``,
                ...briefCandidates.flatMap((c, i) => {
                    const mustCr = c.criteria_analysis?.filter(cr => cr.importance?.toUpperCase().includes('MUST')) ?? [];
                    const mustV  = mustCr.filter(cr => isValidatedStatus(cr.candidate_status)).length;
                    const pitch = getPitch(c);
                    const fullPitch = getFullPitch(c);
                    const achievements = c.top_achievements?.slice(0, 3) ?? [];
                    return [
                        `CANDIDAT ${i + 1} — ${c.name ?? 'N/A'}`,
                        `Titre : ${c.title ?? 'N/A'}`,
                        `Score RevertAI : ${c.score ?? 'N/A'}%  (${mustV}/${mustCr.length} critères clés validés)`,
                        ...(c.availability ? [`Disponibilité : ${c.availability}`] : []),
                        ...((c.tjm_min || c.tjm_max) ? [`TJM : ${c.tjm_min && c.tjm_max ? `${c.tjm_min}–${c.tjm_max}€/j` : c.tjm_min ? `≥${c.tjm_min}€/j` : `≤${c.tjm_max}€/j`}`] : []),
                        ``,
                        ...(pitch ? [`Résumé exécutif :`, pitch, ``] : []),
                        ...(achievements.length > 0 ? [`Réalisations clés :`, ...achievements.map(a => `  • ${a}`), ``] : []),
                        ...(fullPitch ? [`Pourquoi ce profil :`, fullPitch, ``] : []),
                        `─────────────────────────────────────────────────────`,
                        ``,
                    ];
                }),
                `PROCHAINES ÉTAPES`,
                `Pour planifier un entretien, merci d'indiquer vos disponibilités.`,
                `— Équipe RevertAI`,
            ].join('\n');

            return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowBriefModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl z-10 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="font-bold text-slate-900 flex items-center gap-2"><FileDown size={16} className="text-amber-500" /> Note de présentation client</h3>
                                <p className="text-xs text-slate-400">{briefCandidates.length} profil{briefCandidates.length > 1 ? 's' : ''} · prêt à envoyer</p>
                            </div>
                            <button onClick={() => setShowBriefModal(false)} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><X size={18} /></button>
                        </div>
                        {/* Methodology banner */}
                        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 shrink-0">
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                <span className="font-bold text-slate-700">Méthode RevertAI :</span> matching sémantique exhaustif profil × mission sur critères MUST/NICE. Score ≥ 70% = fit validé.
                            </p>
                        </div>
                        {/* Cards */}
                        <div className="overflow-y-auto flex-1 p-5 space-y-4">
                            {briefCandidates.map((c, i) => {
                                const mustCr = c.criteria_analysis?.filter(cr => cr.importance?.toUpperCase().includes('MUST')) ?? [];
                                const mustV  = mustCr.filter(cr => isValidatedStatus(cr.candidate_status)).length;
                                const pitch = getPitch(c);
                                const fullPitch = getFullPitch(c);
                                const achievements = c.top_achievements?.slice(0, 3) ?? [];
                                return (
                                    <div key={c.candidate_id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
                                            <div className="min-w-0">
                                                <p className="font-bold text-slate-900 text-base">{i + 1}. {c.name}</p>
                                                <p className="text-sm text-slate-500 mt-0.5">{c.title}</p>
                                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                    {c.availability && (() => { const av = parseAvailability(c.availability as string | undefined); return (
                                                        <span className={`flex items-center gap-1 text-[10px] font-medium border rounded px-2 py-0.5 ${av.badgeCls}`}>
                                                            <Clock size={9} /> {av.label}
                                                        </span>
                                                    ); })()}
                                                    {(c.tjm_min || c.tjm_max) && (
                                                        <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded px-2 py-0.5">
                                                            <Euro size={9} />
                                                            {c.tjm_min && c.tjm_max ? `${c.tjm_min}–${c.tjm_max}€/j` : c.tjm_min ? `≥${c.tjm_min}€/j` : `≤${c.tjm_max}€/j`}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0 flex flex-col items-end gap-1">
                                                <p className={`text-3xl font-black ${(c.score ?? 0) >= 80 ? 'text-emerald-600' : (c.score ?? 0) >= 70 ? 'text-amber-500' : 'text-slate-600'}`}>{c.score}%</p>
                                                <p className="text-[10px] text-slate-400 font-medium">{mustV}/{mustCr.length} critères clés</p>
                                                <button onClick={() => { navigate(`/talents/${c.candidate_id}`); setShowBriefModal(false); }} className="flex items-center gap-1 text-[10px] font-bold text-amber-600 hover:text-amber-700 mt-1">
                                                    <ExternalLink size={9} /> Voir le profil
                                                </button>
                                            </div>
                                        </div>
                                        <div className="px-5 py-4 space-y-4">
                                            {pitch && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Résumé exécutif</p>
                                                    <p className="text-sm text-slate-800 font-medium leading-snug italic border-l-2 border-amber-400 pl-3">{pitch}</p>
                                                </div>
                                            )}
                                            {achievements.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Réalisations clés</p>
                                                    <ul className="space-y-1.5">
                                                        {achievements.map((a, j) => (
                                                            <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                                                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />{a}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {fullPitch && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Pourquoi ce profil</p>
                                                    <p className="text-sm text-slate-700 leading-relaxed">{fullPitch}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="bg-slate-900 rounded-xl p-4">
                                <p className="text-xs font-bold text-amber-400 uppercase tracking-wide mb-1">Prochaines étapes</p>
                                <p className="text-sm text-slate-300">Pour planifier un entretien avec l'un de ces profils, merci de nous indiquer vos disponibilités.</p>
                            </div>
                        </div>
                        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3 shrink-0">
                            <p className="text-xs text-slate-400">Copier le texte brut pour email ou document</p>
                            <button
                                onClick={() => { navigator.clipboard.writeText(briefText); setCopiedBrief(true); setTimeout(() => setCopiedBrief(false), 2000); }}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                {copiedBrief ? <><Check size={13} /> Copié !</> : <><Copy size={13} /> Copier le texte</>}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}

        {/* ── Outreach IA modal ────────────────────────────────────────────── */}
        {contactCandidate && (() => {
            const c = contactCandidate;
            const outreach: OutreachContent = c.outreach ?? {};
            const emailContent = outreach.email ?? c.sales_pitch ?? '';
            const linkedinShort = outreach.linkedin_dm_short ?? '';
            const linkedinFull  = outreach.linkedin_dm_full ?? '';
            const hook          = outreach.hook ?? '';
            const hasOutreach   = !!(emailContent || linkedinShort || linkedinFull || hook);

            const tabs = [
                { key: 'contact' as const, label: 'Coordonnées', icon: <Phone size={13} />, content: '', color: 'slate' },
                ...(hasOutreach ? [
                    { key: 'linkedin_short' as const, label: 'LinkedIn Court', icon: <Linkedin size={13} />, content: linkedinShort, color: 'sky' },
                    { key: 'linkedin_full' as const,  label: 'LinkedIn Long',  icon: <Linkedin size={13} />, content: linkedinFull,  color: 'sky' },
                    { key: 'email' as const,          label: 'Email',          icon: <Mail size={13} />,     content: emailContent,  color: 'blue' },
                    { key: 'hook' as const,           label: 'Accroche',       icon: <Zap size={13} />,      content: hook,          color: 'amber' },
                ].filter(t => t.content) : []),
            ];

            const activeTab = tabs.find(t => t.key === outreachTab) ?? tabs[0];

            const handleCopy = (text: string, key: string) => {
                navigator.clipboard.writeText(text);
                setCopiedOutreach(key);
                setTimeout(() => setCopiedOutreach(null), 2000);
            };

            const handleGenerateOutreach = async () => {
                if (!c.matching_task_id || !missionId) return;
                setIsGeneratingOutreach(true);
                setOutreachError(null);
                try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-outreach`, {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json', 
                            'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                        },
                        body: JSON.stringify({ candidate_id: c.candidate_id, mission_id: missionId, matching_task_id: c.matching_task_id }),
                    });
                    const json = await res.json();
                    if (!res.ok) throw new Error(json.error ?? 'Erreur génération');
                    const generated = json.outreach as OutreachContent;
                    setOutreachTab('linkedin_short');
                    setContactCandidate({ ...c, outreach: generated });
                } catch (e: any) {
                    setOutreachError(e.message ?? 'Erreur inconnue');
                } finally {
                    setIsGeneratingOutreach(false);
                }
            };

            return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setContactCandidate(null)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-slate-900">{c.name}</h3>
                                <p className="text-xs text-slate-400">{c.title}</p>
                            </div>
                            <button onClick={() => setContactCandidate(null)} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><X size={18} /></button>
                        </div>
                        {/* Tabs */}
                        <div className="flex gap-0 border-b border-slate-100 overflow-x-auto">
                            {tabs.map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setOutreachTab(tab.key)}
                                    className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${
                                        outreachTab === tab.key
                                            ? 'border-slate-900 text-slate-900'
                                            : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-200'
                                    }`}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>
                        {/* Content */}
                        <div className="p-5 min-h-[180px] max-h-[50vh] overflow-y-auto">
                            {activeTab.key === 'contact' ? (
                                <div className="space-y-2">
                                    {c.contact?.phone && (
                                        <div className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl border border-transparent hover:border-slate-200 transition-colors">
                                            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><Phone size={16} /></div>
                                            <div className="flex-1"><div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">Téléphone</div><div className="text-sm text-slate-800 font-medium">{c.contact.phone}</div></div>
                                            <button onClick={() => handleCopy(c.contact!.phone!, 'phone')} className="p-1.5 text-slate-300 hover:text-slate-600 transition-colors">{copiedOutreach === 'phone' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}</button>
                                        </div>
                                    )}
                                    {c.contact?.email && (
                                        <div className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl border border-transparent hover:border-slate-200 transition-colors">
                                            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0"><Mail size={16} /></div>
                                            <div className="flex-1"><div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">Email</div><div className="text-sm text-slate-800 font-medium">{c.contact.email}</div></div>
                                            <button onClick={() => handleCopy(c.contact!.email!, 'email')} className="p-1.5 text-slate-300 hover:text-slate-600 transition-colors">{copiedOutreach === 'email' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}</button>
                                        </div>
                                    )}
                                    {c.contact?.location && (
                                        <div className="flex items-center gap-3 p-3">
                                            <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><MapPin size={16} /></div>
                                            <div className="flex-1"><div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">Localisation</div><div className="text-sm text-slate-800 font-medium">{c.contact.location}</div></div>
                                        </div>
                                    )}
                                    {c.contact?.linkedin && (
                                        <a href={`https://${c.contact.linkedin.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-3 p-3 hover:bg-blue-50/50 rounded-xl border border-transparent hover:border-blue-100 transition-colors">
                                            <div className="w-9 h-9 rounded-full bg-[#0077b5] flex items-center justify-center shrink-0 text-white font-bold text-xs">in</div>
                                            <div className="flex-1"><div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">LinkedIn</div><div className="text-blue-700 font-medium text-sm">Voir le profil</div></div>
                                            <ExternalLink size={14} className="text-slate-300" />
                                        </a>
                                    )}
                                    {!c.contact?.phone && !c.contact?.email && !c.contact?.location && !c.contact?.linkedin && (
                                        <p className="text-sm text-slate-400 text-center py-6">Aucune information de contact disponible.</p>
                                    )}
                                    {!hasOutreach && (
                                        <div className="pt-3 border-t border-slate-100">
                                            {outreachError && <p className="text-xs text-red-500 mb-2">{outreachError}</p>}
                                            <button
                                                onClick={handleGenerateOutreach}
                                                disabled={isGeneratingOutreach}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white rounded-xl text-xs font-bold transition-colors"
                                            >
                                                {isGeneratingOutreach ? <><Loader2 size={13} className="animate-spin" /> Génération en cours…</> : <><Sparkles size={13} /> Générer les messages IA</>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {activeTab.content
                                        ? activeTab.content.split('\n\n').map((para: string, i: number) => (
                                            <p key={i} className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{para}</p>
                                        ))
                                        : <span className="text-slate-400 italic text-sm">Aucun contenu généré pour ce canal.</span>
                                    }
                                </div>
                            )}
                        </div>
                        {/* Footer */}
                        {activeTab.key !== 'contact' && activeTab.content && (
                            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                                <p className="text-xs text-slate-400">Généré par RevertAI — personnaliser avant envoi</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleCopy(activeTab.content, activeTab.key)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors shadow-sm"
                                    >
                                        {copiedOutreach === activeTab.key ? <><Check size={12} className="text-emerald-500" /> Copié</> : <><Copy size={12} /> Copier</>}
                                    </button>
                                    {activeTab.key === 'email' && c.contact?.email && (
                                        <a
                                            href={`mailto:${c.contact.email}?body=${encodeURIComponent(activeTab.content)}`}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors"
                                        >
                                            <Mail size={12} /> Ouvrir dans Mail
                                        </a>
                                    )}
                                    {(activeTab.key === 'linkedin_short' || activeTab.key === 'linkedin_full') && c.contact?.linkedin && (
                                        <a
                                            href={`https://${c.contact.linkedin.replace(/^https?:\/\//, '')}`}
                                            target="_blank" rel="noreferrer"
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0077b5] text-white rounded-lg text-xs font-bold hover:bg-[#005e93] transition-colors"
                                        >
                                            <Linkedin size={12} /> Ouvrir LinkedIn
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        })()}
        </div>
    );
};

export default MissionMatchResults;
